import React, { useContext, useEffect, useRef, useState } from "react";
import { useApolloClient, useQuery } from "@apollo/client";
import type { ApolloClient } from "@apollo/client";
import { Elements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { contextObject } from "../../components/shared/ShoppingCart/ShoppingCartContext";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Divider from "@mui/material/Divider";
import { currencyFormat, groupCartItems } from "../../components/utils/utils";
import Button from "@mui/material/Button";
import { useQueryShippingMethods } from "../../queries/product/hooks/useQueryShippingMethod";
import { CircularProgress } from "@mui/material";
import { useMutationCreateManyOrders } from "../../queries/order/hooks/useMutationCreateManyOrders";
import { useMutationPrepareCardCheckoutPayment } from "../../queries/order/hooks/useMutationPrepareCardCheckoutPayment";
import { prepareAndConfirmCardCheckout } from "./checkoutCardAuthorization";
import { UserContext } from "../../hooks/userContext";
import { PharmacyContext } from "../../hooks/pharmacyContext";
import { ToasterContext } from "../../components/shared/Toaster/ToasterContext";
import { NotificationType } from "../../constants/global";
import { CART, DELETE_ALL_CART, POSTINGS } from "../../queries/product/productTypeDefs";
import { useMutationDeleteAllCart } from "../../queries/product/hooks/useMutationDeleteAllCart";
import { routeConfigs } from "../../navigation/routeConfig";
import { Navigate, useNavigate } from "react-router-dom";
import CheckoutGroupItem from "./CheckoutGroupItem";
import { useQueryBankAccount } from "../../queries/pharmacy/hooks/useQueryBankAccount";
import { CHECKOUT_PHARMACY } from "../../queries/pharmacy/pharmacyTypeDefs";
import { CheckoutPaymentChoice, CheckoutPaymentMethodBox } from "./CheckoutPaymentMethodBox";
import { CheckoutOneOffCardDialog } from "./CheckoutOneOffCardDialog";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY || "");

const FREE_PRIORITY_SHIPPING_MIN_SUBTOTAL = 3000;
const PRIORITY_OVERNIGHT_METHOD_KEY = "fedex-priority-overnight";

const CART_QUERY_VARIABLES = (userId: number) => ({
  where: { userId: { equals: userId } },
  orderBy: { createdAt: "asc" as const },
});

function getGraphqlErrorMessage(error: unknown): string {
  if (error && typeof error === "object") {
    const gql = (error as any).graphQLErrors?.[0]?.message;
    if (typeof gql === "string" && gql.length > 0) {
      return gql;
    }
    const msg = (error as any).message;
    if (typeof msg === "string" && msg.length > 0) {
      return msg;
    }
  }
  return "Failed to place order.";
}

function isStockConflictMessage(message: string): boolean {
  const m = message.toLowerCase();
  return (
    m.includes("no longer in stock") ||
    m.includes("insufficient stock") ||
    m.includes("refresh your cart")
  );
}

async function removeUnbuyableCartLines(apolloClient: ApolloClient<object>, userId: number): Promise<void> {
  const cartVars = CART_QUERY_VARIABLES(userId);

  const { data: cartData } = await apolloClient.query({
    query: CART,
    variables: cartVars,
    fetchPolicy: "network-only",
  });

  const rows: any[] = cartData?.findManyCart || [];
  if (rows.length === 0) {
    return;
  }

  const productIds = Array.from(new Set(rows.map((r) => r.productId).filter(Boolean))) as number[];
  if (productIds.length === 0) {
    return;
  }

  const { data: prodData } = await apolloClient.query({
    query: POSTINGS,
    variables: {
      where: { id: { in: productIds } },
    },
    fetchPolicy: "network-only",
  });

  const products: any[] = prodData?.findManyProduct || [];
  const byId: Record<number, any> = {};
  products.forEach((p: any) => {
    byId[p.id] = p;
  });

  const productIdsToRemove = new Set<number>();
  for (const row of rows) {
    const pid = row.productId as number;
    const p = byId[pid];
    const requested = Number(row.quantity || 0);
    if (!p || p.isActive === false) {
      productIdsToRemove.add(pid);
      continue;
    }
    const available = Number(p.quantity || 0);
    if (available <= 0 || requested > available) {
      productIdsToRemove.add(pid);
    }
  }

  if (productIdsToRemove.size === 0) {
    return;
  }

  const idsToRemoveFromCart: number[] = [];
  productIdsToRemove.forEach((pid) => idsToRemoveFromCart.push(pid));

  await apolloClient.mutate({
    mutation: DELETE_ALL_CART,
    variables: {
      where: {
        AND: [{ userId: { equals: userId } }, { productId: { in: idsToRemoveFromCart } }],
      },
    },
    refetchQueries: [{ query: CART, variables: cartVars }],
    awaitRefetchQueries: true,
  });
}

const CheckoutInner: React.FunctionComponent = () => {
  const { id }: any = useContext(UserContext);
  const { pharmacyId }: any = useContext(PharmacyContext);
  const { addToast } = useContext(ToasterContext);
  const { cart } = useContext(contextObject);
  const [cartGroupData, setCartGroupData] = useState<any>([]);
  const navigate = useNavigate();
  const primePostFee = 12.5;
  const [loading, setLoading] = useState<boolean>(false);
  const [confirmDisable, setConfirmDisable] = useState<boolean>(false);
  const [fedexError, setFedexError] = useState<any>({});
  const [paymentChoice, setPaymentChoice] = useState<CheckoutPaymentChoice>("saved");
  const [cardDialogOpen, setCardDialogOpen] = useState(false);
  const apolloClient = useApolloClient();
  const stripe = useStripe();
  const orderSubmitLockRef = useRef(false);

  const { data: shippingMethodsData, loading: shippingMethodsLoading } = useQueryShippingMethods();
  const [createOrders, { createOrdersLoading }] = useMutationCreateManyOrders();
  const [prepareCardCheckoutPayment] = useMutationPrepareCardCheckoutPayment();
  const [deleteAllCartItems, { deleteLoading }] = useMutationDeleteAllCart({
    refetchQueries: [
      {
        query: CART,
        variables: {
          where: { userId: { equals: +id } },
          orderBy: { createdAt: "asc" },
        },
        awaitRefetchQueries: true,
      },
    ],
  });
  const { data: bankAccountData, loading: bankAccountLoading } = useQueryBankAccount({
    variables: {
      where: {
        AND: [
          { object: { in: ["payment_method", "card"] } },
          { userId: { equals: +id } },
          { status: { in: ["VERIFIED", "APPROVED"] } },
          { accountId: { not: { equals: null } } },
        ],
      },
    },
    fetchPolicy: "no-cache",
  });
  const { data: pharmacyData, loading: pharmacyLoading } = useQuery(CHECKOUT_PHARMACY, {
    variables: { where: { id: +pharmacyId } },
    skip: !pharmacyId,
    fetchPolicy: "network-only",
  });

  const cardPaymentEnabled = pharmacyData?.findUniquePharmacy?.cardPaymentEnabled === true;
  const buyerEmail = pharmacyData?.findUniquePharmacy?.email;

  useEffect(() => {
    if (!cardPaymentEnabled && paymentChoice === "one_off") {
      setPaymentChoice("saved");
    }
  }, [cardPaymentEnabled, paymentChoice]);

  const getCartItemTotal = (items: any) => {
    let total = 0;
    items.forEach((item: any) => {
      total += item.quantity * Number(item.item.price.replace(",", ""));
    });
    return total;
  };

  const getGrandTotal = () => {
    let grandTotal = 0;
    const freeShippingPharmacyIds = getFreePriorityShippingPharmacyIds();

    cartGroupData.forEach((cartGroupDataItem: any) => {
      const methodKey = cartGroupDataItem?.shippingMethod?.key;
      const itemsOnly =
        methodKey === "fedex-ground" || freeShippingPharmacyIds.has(cartGroupDataItem.pharmacyId);
      const totalWithoutShipping = itemsOnly
        ? getCartItemTotal(cartGroupDataItem.groupItem)
        : Number(cartGroupDataItem.total ?? getCartItemTotal(cartGroupDataItem.groupItem));

      grandTotal += totalWithoutShipping;
    });

    return grandTotal.toFixed(2);
  };

  const getAppliedShippingFee = (cartGroupDataItem: any, freeShippingPharmacyIds: Set<unknown>) => {
    const methodKey = cartGroupDataItem?.shippingMethod?.key;
    if (methodKey === "fedex-ground" || freeShippingPharmacyIds.has(cartGroupDataItem.pharmacyId)) {
      return 0;
    }
    return Number(cartGroupDataItem.fee || 0);
  };

  const getCombinedItemSubtotal = () =>
    cartGroupData.reduce((sum: number, group: any) => sum + getCartItemTotal(group.groupItem), 0);

  const getCombinedShipping = () => {
    const freeShippingPharmacyIds = getFreePriorityShippingPharmacyIds();
    return cartGroupData.reduce(
      (sum: number, group: any) => sum + getAppliedShippingFee(group, freeShippingPharmacyIds),
      0
    );
  };

  const getShippingData: any = (cartItemId: number) => {
    let data: any = {};
    cartGroupData.every((cartGroupDataItem: any) => {
      let totalQuantity = 0;
      cartGroupDataItem.groupItem.forEach((item: any) => {
        totalQuantity = totalQuantity + item.quantity;
      });

      const unitFee = cartGroupDataItem.fee / totalQuantity;

      const foundItem = cartGroupDataItem.groupItem.find((item: any) => item.id === cartItemId);

      if (foundItem) {
        data = {
          fee: (foundItem.quantity * unitFee).toFixed(2),
          shippingMethodId: cartGroupDataItem.shippingMethod.id,
        };
        return false;
      }

      return true;
    });

    return data;
  };

  const getFreePriorityShippingPharmacyIds = () => {
    const eligibleGroups = cartGroupData.filter((cartGroupDataItem: any) => {
      const subtotal = getCartItemTotal(cartGroupDataItem.groupItem);
      return (
        cartGroupDataItem?.shippingMethod?.key === PRIORITY_OVERNIGHT_METHOD_KEY &&
        subtotal >= FREE_PRIORITY_SHIPPING_MIN_SUBTOTAL
      );
    });

    return new Set(eligibleGroups.map((item: any) => item.pharmacyId));
  };

  const canSubmitOrder =
    !createOrdersLoading && !deleteLoading && !loading && !confirmDisable && Object.keys(fedexError).length === 0;

  const validateBeforeOrder = (): boolean => {
    const grandTotal = getGrandTotal();
    if (parseFloat(grandTotal) < 100) {
      addToast({ type: NotificationType.Error, title: "Order total must be greater than $100." });
      return false;
    }

    if (Object.keys(fedexError).length !== 0) {
      return false;
    }

    return true;
  };

  const usesCardPaymentForCheckout = (
    oneOffPaymentMethodId?: string,
    savedBankAccount?: { object?: string | null }
  ) =>
    Boolean(oneOffPaymentMethodId) ||
    (cardPaymentEnabled && savedBankAccount?.object === "card");

  const placeOrder = async (
    oneOffPaymentMethodId?: string,
    options?: { lockAlreadyHeld?: boolean; savedBankAccount?: { object?: string | null; accountId?: string | null } }
  ) => {
    if (!options?.lockAlreadyHeld) {
      if (orderSubmitLockRef.current || loading || createOrdersLoading || deleteLoading) {
        return;
      }
      orderSubmitLockRef.current = true;
      setLoading(true);
    }

    try {
      const productIds = cart.map((c: any) => c.item.id);
      const requestedByProductId: Record<number, number> = {};
      cart.forEach((c: any) => {
        requestedByProductId[c.item.id] = (requestedByProductId[c.item.id] || 0) + Number(c.quantity || 0);
      });

      const { data: availabilityData } = await apolloClient.query({
        query: POSTINGS,
        variables: {
          where: { id: { in: productIds } },
        },
        fetchPolicy: "network-only",
      });

      const products: any[] = availabilityData?.findManyProduct || [];

      const byId: Record<number, any> = {};
      products.forEach((p: any) => {
        byId[p.id] = p;
      });

      const outOfStock: { id: number; name: string; available: number; requested: number; reason: string }[] = [];
      productIds.forEach((pid: number) => {
        const p = byId[pid];
        const requested = requestedByProductId[pid] || 0;
        if (!p) {
          outOfStock.push({ id: pid, name: `Product #${pid}`, available: 0, requested, reason: "unavailable" });
          return;
        }
        if (p.isActive === false) {
          outOfStock.push({ id: pid, name: p.drugName || `Product #${pid}`, available: Number(p.quantity || 0), requested, reason: "inactive" });
          return;
        }
        const available = Number(p.quantity || 0);
        if (available <= 0 || requested > available) {
          outOfStock.push({ id: pid, name: p.drugName || `Product #${pid}`, available, requested, reason: "insufficient" });
        }
      });

      if (outOfStock.length > 0) {
        const msg = outOfStock
          .map((o) => `${o.name}: requested ${o.requested}, available ${o.available}`)
          .join("; ");
        addToast({ type: NotificationType.Error, title: `Some items are no longer available: ${msg}` });
        return;
      }

      const requestData: any = [];

      cart.forEach((cartItem: any) => {
        requestData.push({
          buyerId: pharmacyId,
          buyerUserId: +id,
          isConfirmed: false,
          orderNumber: `${Math.floor(Date.now() / 1000)}`,
          paymentStatus: 0,
          price: cartItem.item.price,
          productId: cartItem.item.id,
          quantity: cartItem.quantity,
          sellerId: cartItem.item.Pharmacy.id,
          sellerUserId: cartItem.item.User.id,
          shippingFee: `${getShippingData(cartItem.id).fee}`,
          shippingMethodId: getShippingData(cartItem.id).shippingMethodId,
          shippingStatus: 0,
          status: 1,
          totalAmount: `${Number(cartItem.item.price.replace(",", "")) * cartItem.quantity}`,
          primePostFee: `${primePostFee}`,
        });
      });

      let cardPaymentIntentIds: string[] | null = null;
      let orderOneOffPaymentMethodId = oneOffPaymentMethodId ?? null;
      const savedBankAccount = options?.savedBankAccount;
      const isCardCheckout = usesCardPaymentForCheckout(
        orderOneOffPaymentMethodId ?? undefined,
        savedBankAccount
      );

      if (isCardCheckout) {
        if (!stripe) {
          throw new Error("Card payment is not ready yet. Please wait a moment.");
        }

        const cardAuth = await prepareAndConfirmCardCheckout({
          stripe,
          orderData: requestData,
          oneOffPaymentMethodId: orderOneOffPaymentMethodId,
          paymentMethodId: orderOneOffPaymentMethodId ?? savedBankAccount?.accountId ?? null,
          prepareCardCheckoutPayment,
        });
        cardPaymentIntentIds = cardAuth.cardPaymentIntentIds;
        orderOneOffPaymentMethodId = cardAuth.oneOffPaymentMethodId ?? null;
      }

      await createOrders({
        variables: {
          data: requestData,
          oneOffPaymentMethodId: orderOneOffPaymentMethodId,
          cardPaymentIntentIds,
        },
      });

      await deleteAllCartItems({ variables: { where: { userId: { equals: +id } } } });

      navigate(routeConfigs.buyHistory.path);
      addToast({ type: NotificationType.Success, title: "Your order has been successfully created." });
    } catch (e: any) {
      const errMsg = getGraphqlErrorMessage(e);
      addToast({ type: NotificationType.Error, title: errMsg });
      if (isStockConflictMessage(errMsg)) {
        try {
          await removeUnbuyableCartLines(apolloClient, +id);
        } catch {
          /* cleanup is best-effort */
        }
      }
    } finally {
      orderSubmitLockRef.current = false;
      setLoading(false);
    }
  };

  const handleOrderConfirm = () => {
    if (!canSubmitOrder || orderSubmitLockRef.current) {
      return;
    }

    if (!validateBeforeOrder()) {
      return;
    }

    if (cardPaymentEnabled && paymentChoice === "one_off") {
      setCardDialogOpen(true);
      return;
    }

    void placeOrder(undefined, { savedBankAccount: bankAccountData?.findFirstBankAccount });
  };

  const handleOneOffCardConfirm = async (oneOffPaymentMethodId?: string) => {
    if (orderSubmitLockRef.current) {
      return;
    }
    orderSubmitLockRef.current = true;
    setLoading(true);
    setCardDialogOpen(false);
    if (!oneOffPaymentMethodId) {
      setPaymentChoice("saved");
    }
    await placeOrder(oneOffPaymentMethodId, {
      lockAlreadyHeld: true,
      savedBankAccount: bankAccountData?.findFirstBankAccount,
    });
  };

  if (shippingMethodsLoading || bankAccountLoading || pharmacyLoading) {
    return <CircularProgress sx={{ position: "absolute", left: "calc(150px + 50%)", top: "50%" }} color="inherit" />;
  }

  if (!bankAccountData?.findFirstBankAccount) {
    return <Navigate to={`${routeConfigs.bankAccount.path}/?required=true`} />;
  }

  const bankAccount = bankAccountData.findFirstBankAccount;

  return (
    <Box>
      <Box sx={{ mb: 2, fontWeight: "bold", fontSize: "20px", textAlign: "center" }}>Checkout ({cart.length} Items)</Box>
      {Object.keys(groupCartItems(cart)).map((name: any) => (
        <CheckoutGroupItem
          key={name}
          name={name}
          groupItem={groupCartItems(cart)[name]}
          getGroupData={(data: any) => {
            const newArr = [...cartGroupData];
            const foundIndex = cartGroupData.findIndex((item: any) => item.pharmacyId === data.pharmacyId);

            if (foundIndex > -1) {
              newArr.splice(foundIndex, 1);
            }

            setCartGroupData([...newArr, data]);
          }}
          shippingMethodsData={shippingMethodsData.findManyShippingMethod}
          cartGroupData={cartGroupData}
          setCartGroupData={setCartGroupData}
          setConfirmDisable={setConfirmDisable}
          setFedexError={setFedexError}
          fedexError={fedexError}
          freePriorityShippingApplied={getFreePriorityShippingPharmacyIds().has(groupCartItems(cart)[name][0].item.Pharmacy.id)}
        />
      ))}
      {cart.length > 0 && (
        <Box
          sx={{
            mt: 4,
            mb: 4,
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 3,
          }}
        >
          <Box sx={{ flex: "1 1 320px", minWidth: 0, maxWidth: 440 }}>
            <CheckoutPaymentMethodBox
              bankAccount={bankAccount}
              cardPaymentEnabled={cardPaymentEnabled}
              paymentChoice={paymentChoice}
              onPaymentChoiceChange={setPaymentChoice}
            />
          </Box>
          <CheckoutOneOffCardDialog
            open={cardDialogOpen}
            buyerEmail={buyerEmail}
            submitting={loading || createOrdersLoading || deleteLoading}
            savedBankAccount={bankAccount}
            pharmacyId={+pharmacyId}
            userId={+id}
            onClose={() => setCardDialogOpen(false)}
            onConfirm={handleOneOffCardConfirm}
          />
          <Paper
            elevation={0}
            sx={{
              flex: "1 1 300px",
              width: "100%",
              maxWidth: 380,
              ml: "auto",
              p: 2.5,
              border: "1px solid #e2e8f0",
              borderRadius: 2,
            }}
          >
            <Box sx={{ fontWeight: 700, fontSize: 16, color: "#0f172a", mb: 1.5 }}>Order Summary</Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, mb: 1, color: "#334155" }}>
              <Box>Item Subtotal</Box>
              <Box>{currencyFormat(getCombinedItemSubtotal())}</Box>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, mb: 1.5, color: "#334155" }}>
              <Box>Shipping</Box>
              <Box>{currencyFormat(getCombinedShipping())}</Box>
            </Box>
            <Divider sx={{ borderColor: "#e2e8f0" }} />
            <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, mt: 1.5, fontWeight: 700, fontSize: 18, color: "#0f172a" }}>
              <Box>Grand Total</Box>
              <Box>{currencyFormat(Number(getGrandTotal()))}</Box>
            </Box>
            <Button
              color="success"
              variant="contained"
              fullWidth
              size="large"
              onClick={handleOrderConfirm}
              disabled={!canSubmitOrder}
              sx={{ mt: 2, minHeight: 48, fontWeight: 700, textTransform: "none" }}
            >
              Confirm
              {(createOrdersLoading || deleteLoading || loading) && (
                <CircularProgress sx={{ ml: 1.5 }} size={14} color="inherit" />
              )}
            </Button>
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export const Checkout: React.FunctionComponent = () => (
  <Elements stripe={stripePromise}>
    <CheckoutInner />
  </Elements>
);

export default Checkout;
